import { createContext, useEffect, useReducer, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { REST_API } from 'src/constants/DefaultValues';

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;
    return {
      ...state,
      isAuthenticated: true,
      user
    };
  }
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
  refreshUser: () => Promise.resolve(),
  forgotPassword: () => Promise.resolve(),
  verifyOtp: () => Promise.resolve(),
  resetPassword: () => Promise.resolve()
});

AuthProvider.propTypes = {
  children: PropTypes.node
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const response = await axios.get(`${REST_API}auth/me`, { withCredentials: true });
        const { user } = response.data;
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: true,
            user
          }
        });
      } catch (err) {
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null
          }
        });
      }
    };

    initialize();
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated || !state.user) return undefined;

    const userType = Number(state.user.user_type);
    if (userType !== 2 && userType !== 3) return undefined;

    let watchId = null;
    let lastSentAt = 0;
    const MIN_SEND_INTERVAL_MS = userType === 3 ? 5000 : 60000;

    const postLocation = async (latitude, longitude) => {
      const now = Date.now();
      if (now - lastSentAt < MIN_SEND_INTERVAL_MS) return;
      lastSentAt = now;
      try {
        const endpoint = userType === 2 ? 'customers/update-location' : 'drivers/update-location';
        await axios.post(
          `${REST_API}${endpoint}`,
          { latitude, longitude, user_id: state.user.id },
          { withCredentials: true }
        );
      } catch (error) {
        console.error('Error updating location:', error);
      }
    };

    if (!navigator.geolocation) {
      console.warn('Geolocation not supported');
      return undefined;
    }

    // Drivers: continuous GPS for live customer tracking
    if (userType === 3) {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          postLocation(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 3000 }
      );
    } else {
      // Customers: one-shot current location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          postLocation(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Geolocation error:', error);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    }

    return () => {
      if (watchId != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, [state.isAuthenticated, state.user]);

  const login = async (email, password) => {
    const response = await axios.post(`${REST_API}login`, { email, password }, { withCredentials: true });
    const { user } = response.data;
    dispatch({
      type: 'LOGIN',
      payload: {
        user
      }
    });

    return response.data;
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post(`${REST_API}signup`, {
      email,
      password,
      firstName,
      lastName
    }, { withCredentials: true });

    const { user } = response.data;
    dispatch({
      type: 'REGISTER',
      payload: {
        user
      }
    });
  };

  const logout = async () => {
    await axios.post(`${REST_API}logout`, {}, { withCredentials: true });
    dispatch({
      type: 'LOGOUT', payload: {
        user: null
      }
    });
  };

  const forgotPassword = async (email) => {
    const response = await axios.post(`${REST_API}forgot-password`, { email });
    return response.data;
  };

  const verifyOtp = async (email, otp) => {
    const response = await axios.post(`${REST_API}verify-otp`, { email, otp });
    return response.data;
  };

  const resetPassword = async (email, otp, new_password) => {
    const response = await axios.post(`${REST_API}reset-password`, { email, otp, new_password });
    return response.data;
  };

  const updateProfile = () => { };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${REST_API}auth/me`, { withCredentials: true });
      const { user } = response.data;
      dispatch({
        type: 'LOGIN',
        payload: {
          user
        }
      });
      return user;
    } catch (err) {
      console.error('Error refreshing user data:', err);
      return null;
    }
  };
  // const user = localStorage.getItem('user');
  // const user = JSON.parse(localStorage.getItem('user'));

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
        register,
        updateProfile,
        refreshUser,
        forgotPassword,
        verifyOtp,
        resetPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
